import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { translationsApi } from '@/api';
import {
  extractApiError,
  extractApiErrorWithTierNumbers,
  type ApiErrorWithTierNumbers,
} from '@/api/errorStatus';
import type { QuizTranslationDto, PaywallTranslationDto } from '@/api';
import { rejectedApiError } from './rejectedApiError';

export interface TranslationState {
  currentLang: string | null;
  quizTranslations: QuizTranslationDto[];
  paywallTranslations: PaywallTranslationDto[];
  isLoading: boolean;
  /** WEB-1955: разобранный отказ — фразу человеку выбирает экран по коду. */
  error: TranslationErrorPayload | null;
}

const initialState: TranslationState = {
  currentLang: null,
  quizTranslations: [],
  paywallTranslations: [],
  isLoading: false,
  error: null,
};

// WEB-1320: rejectWithValue payload shared by thunks whose failures a caller
// may need to branch on (e.g. `dispatch(...).unwrap()` in LanguageSwitcher
// distinguishing HTTP 402 "insufficient credits" from any other failure).
// The api client (see src/api/client.ts) flattens the HTTP status onto the
// thrown Error before it reaches this catch block.
//
// ВТОРОЙ КОНЕЦ WEB-1954 (ревизия пачки 28, R2): пейлоад был собран здесь
// руками из двух полей — `{ message, status }`, — и `code`/`reasonCode`
// умирали в слайсе. А текст отказа человеку кабинет берёт ИМЕННО по коду
// (`localizeError`, правило WEB-1966): на 403 `RESOURCE_READONLY` человек
// читал общее «Не удалось перевести». Форма отказа теперь одна на систему —
// разобранный `extractApiError` (та же правка, что U6 сделал соседу
// `editorSlice.changeSourceLanguage`); числа тарифа (`usage`/`limit`) идут
// тем же общим правилом, что и у остальных слайсов.
export type TranslationErrorPayload = ApiErrorWithTierNumbers;

/**
 * WEB-1955: причина отказа в состоянии — РАЗОБРАННАЯ, а не текст.
 *
 * Редьюсер брал из пейлоада ОДИН текст (а без пейлоада — текст
 * сериализованной ошибки) и клал его в `state.error`. Код при этом умирал
 * здесь же: экран, читающий состояние, спросить реестр уже не мог. Пейлоад
 * тханка отдаётся как есть (в нём есть и числа тарифа), а если его нет —
 * отказ разбирает общая дверь `rejectedApiError`.
 */
function rejectedTranslationError(action: {
  payload?: unknown;
  error?: unknown;
}): TranslationErrorPayload {
  const payload = action.payload as TranslationErrorPayload | undefined;
  return payload ?? rejectedApiError(action);
}

function toTranslationError(error: unknown, fallback: string): TranslationErrorPayload {
  return extractApiErrorWithTierNumbers(error, fallback);
}

// Quiz async thunks
export const loadQuizTranslations = createAsyncThunk(
  'translation/loadQuizTranslations',
  async (
    { projectId, quizId }: { projectId: string; quizId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await translationsApi.listQuizTranslations(projectId, quizId);
      const data = response.data as { translations: QuizTranslationDto[] };
      return data.translations;
    } catch (error: unknown) {
      // Тот же разобранный отказ, что у остальных ветвей слайса: список
      // переводов упирается в те же коды (403 при понижении тарифа), и
      // терять их на полпути незачем.
      return rejectWithValue(extractApiError(error));
    }
  },
);

export const translateQuiz = createAsyncThunk(
  'translation/translateQuiz',
  async (
    {
      projectId,
      quizId,
      targetLanguage,
      model = {},
    }: {
      projectId: string;
      quizId: string;
      targetLanguage: string;
      /** WEB-2824: поле модели из одной двери `modelIdForRequest`. */
      model?: { modelId?: string };
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await translationsApi.translateQuiz(
        projectId,
        quizId,
        targetLanguage,
        model,
      );
      return response.data as QuizTranslationDto;
    } catch (error: unknown) {
      return rejectWithValue(toTranslationError(error, 'Failed to translate quiz'));
    }
  },
);

export const updateQuizTranslation = createAsyncThunk(
  'translation/updateQuizTranslation',
  async (
    {
      projectId,
      quizId,
      lang,
      content,
      baseContentVersion,
    }: {
      projectId: string;
      quizId: string;
      lang: string;
      content: object[];
      /** WEB-1653: отпечаток содержимого, снятый окном правки при открытии. */
      baseContentVersion?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await translationsApi.updateQuizTranslation(
        projectId,
        quizId,
        lang,
        content,
        baseContentVersion,
      );
      return response.data as QuizTranslationDto;
    } catch (error: unknown) {
      // WEB-1653: отказ обязан донести КОД СОСТОЯНИЯ, а не одну строку —
      // окно правки разводит 409 («догон переписал содержимое, перезагрузи
      // окно») и любую другую поломку. Раньше здесь ехала голая строка,
      // поэтому окно даже серверный текст показать не могло.
      return rejectWithValue(
        toTranslationError(error, 'Failed to update translation'),
      );
    }
  },
);

export const deleteQuizTranslation = createAsyncThunk(
  'translation/deleteQuizTranslation',
  async (
    { projectId, quizId, lang }: { projectId: string; quizId: string; lang: string },
    { rejectWithValue },
  ) => {
    try {
      await translationsApi.deleteQuizTranslation(projectId, quizId, lang);
      return lang;
    } catch (error: unknown) {
      return rejectWithValue(toTranslationError(error, 'Failed to delete translation'));
    }
  },
);

// Paywall async thunks
export const loadPaywallTranslations = createAsyncThunk(
  'translation/loadPaywallTranslations',
  async (
    { projectId, paywallId }: { projectId: string; paywallId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await translationsApi.listPaywallTranslations(projectId, paywallId);
      const data = response.data as { translations: PaywallTranslationDto[] };
      return data.translations;
    } catch (error: unknown) {
      /** Зеркало квизовой стороны — см. loadQuizTranslations. */
      return rejectWithValue(extractApiError(error));
    }
  },
);

export const translatePaywall = createAsyncThunk(
  'translation/translatePaywall',
  async (
    {
      projectId,
      paywallId,
      targetLanguage,
      model = {},
    }: {
      projectId: string;
      paywallId: string;
      targetLanguage: string;
      /** WEB-2824: поле модели из одной двери `modelIdForRequest`. */
      model?: { modelId?: string };
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await translationsApi.translatePaywall(
        projectId,
        paywallId,
        targetLanguage,
        model,
      );
      return response.data as PaywallTranslationDto;
    } catch (error: unknown) {
      return rejectWithValue(toTranslationError(error, 'Failed to translate paywall'));
    }
  },
);

export const updatePaywallTranslation = createAsyncThunk(
  'translation/updatePaywallTranslation',
  async (
    {
      projectId,
      paywallId,
      lang,
      content,
      baseContentVersion,
    }: {
      projectId: string;
      paywallId: string;
      lang: string;
      content: object[];
      /** WEB-1653 — см. updateQuizTranslation. */
      baseContentVersion?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await translationsApi.updatePaywallTranslation(
        projectId,
        paywallId,
        lang,
        content,
        baseContentVersion,
      );
      return response.data as PaywallTranslationDto;
    } catch (error: unknown) {
      /** WEB-1653 — зеркало квизовой стороны: окну нужен код состояния. */
      return rejectWithValue(
        toTranslationError(error, 'Failed to update translation'),
      );
    }
  },
);

export const deletePaywallTranslation = createAsyncThunk(
  'translation/deletePaywallTranslation',
  async (
    { projectId, paywallId, lang }: { projectId: string; paywallId: string; lang: string },
    { rejectWithValue },
  ) => {
    try {
      await translationsApi.deletePaywallTranslation(projectId, paywallId, lang);
      return lang;
    } catch (error: unknown) {
      return rejectWithValue(toTranslationError(error, 'Failed to delete translation'));
    }
  },
);

const translationSlice = createSlice({
  name: 'translation',
  initialState,
  reducers: {
    setCurrentLang(state, action: PayloadAction<string | null>): TranslationState {
      return { ...state, currentLang: action.payload };
    },
    clearTranslations(): TranslationState {
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    builder
      // loadQuizTranslations
      .addCase(loadQuizTranslations.pending, (state) => ({
        ...state,
        isLoading: true,
        error: null,
      }))
      .addCase(loadQuizTranslations.fulfilled, (state, action) => ({
        ...state,
        isLoading: false,
        quizTranslations: action.payload,
      }))
      .addCase(loadQuizTranslations.rejected, (state, action) => ({
        ...state,
        isLoading: false,
        error: rejectedTranslationError(action),
      }))
      // translateQuiz
      .addCase(translateQuiz.pending, (state) => ({ ...state, isLoading: true, error: null }))
      .addCase(translateQuiz.fulfilled, (state, action) => {
        const existing = state.quizTranslations.find(
          (t) => t.language === action.payload.language,
        );
        const updated = existing
          ? state.quizTranslations.map((t) =>
              t.language === action.payload.language ? action.payload : t,
            )
          : [...state.quizTranslations, action.payload];
        return {
          ...state,
          isLoading: false,
          quizTranslations: updated,
          currentLang: action.payload.language,
        };
      })
      .addCase(translateQuiz.rejected, (state, action) => ({
        ...state,
        isLoading: false,
        error: rejectedTranslationError(action),
      }))
      // updateQuizTranslation
      .addCase(updateQuizTranslation.fulfilled, (state, action) => ({
        ...state,
        quizTranslations: state.quizTranslations.map((t) =>
          t.language === action.payload.language ? action.payload : t,
        ),
      }))
      // deleteQuizTranslation
      .addCase(deleteQuizTranslation.fulfilled, (state, action) => ({
        ...state,
        quizTranslations: state.quizTranslations.filter((t) => t.language !== action.payload),
        currentLang: state.currentLang === action.payload ? null : state.currentLang,
      }))
      .addCase(deleteQuizTranslation.rejected, (state, action) => ({
        ...state,
        isLoading: false,
        error: rejectedTranslationError(action),
      }))
      // loadPaywallTranslations
      .addCase(loadPaywallTranslations.pending, (state) => ({
        ...state,
        isLoading: true,
        error: null,
      }))
      .addCase(loadPaywallTranslations.fulfilled, (state, action) => ({
        ...state,
        isLoading: false,
        paywallTranslations: action.payload,
      }))
      .addCase(loadPaywallTranslations.rejected, (state, action) => ({
        ...state,
        isLoading: false,
        error: rejectedTranslationError(action),
      }))
      // translatePaywall
      .addCase(translatePaywall.pending, (state) => ({ ...state, isLoading: true, error: null }))
      .addCase(translatePaywall.fulfilled, (state, action) => {
        const existing = state.paywallTranslations.find(
          (t) => t.language === action.payload.language,
        );
        const updated = existing
          ? state.paywallTranslations.map((t) =>
              t.language === action.payload.language ? action.payload : t,
            )
          : [...state.paywallTranslations, action.payload];
        return {
          ...state,
          isLoading: false,
          paywallTranslations: updated,
          currentLang: action.payload.language,
        };
      })
      .addCase(translatePaywall.rejected, (state, action) => ({
        ...state,
        isLoading: false,
        error: rejectedTranslationError(action),
      }))
      // updatePaywallTranslation
      .addCase(updatePaywallTranslation.fulfilled, (state, action) => ({
        ...state,
        paywallTranslations: state.paywallTranslations.map((t) =>
          t.language === action.payload.language ? action.payload : t,
        ),
      }))
      // deletePaywallTranslation
      .addCase(deletePaywallTranslation.fulfilled, (state, action) => ({
        ...state,
        paywallTranslations: state.paywallTranslations.filter(
          (t) => t.language !== action.payload,
        ),
        currentLang: state.currentLang === action.payload ? null : state.currentLang,
      }))
      .addCase(deletePaywallTranslation.rejected, (state, action) => ({
        ...state,
        isLoading: false,
        error: rejectedTranslationError(action),
      }));
  },
});

export const { setCurrentLang, clearTranslations } = translationSlice.actions;

// Selectors
export const selectTranslationState = (state: { translation: TranslationState }) =>
  state.translation;

export const selectCurrentLang = createSelector(
  selectTranslationState,
  (s) => s.currentLang,
);

export const selectQuizTranslations = createSelector(
  selectTranslationState,
  (s) => s.quizTranslations,
);

export const selectPaywallTranslations = createSelector(
  selectTranslationState,
  (s) => s.paywallTranslations,
);

export const selectTranslationLoading = createSelector(
  selectTranslationState,
  (s) => s.isLoading,
);

// WEB-1320: errors are shown imperatively via antd `message` in LanguageSwitcher
// (dispatch(...).unwrap() + try/catch), not read from this selector — there is
// no display consumer. `error` stays populated on every rejected branch above
// as a diagnostic field only.
export const selectTranslationError = createSelector(
  selectTranslationState,
  (s) => s.error,
);

export default translationSlice.reducer;
